import Typography from 'src/components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import FormatService from 'src/services/format.service';
import React from 'react';
import styles from './BudgetProgressBar.module.scss';
import { BudgetSummaryStatus } from 'src/types/budget-summary-status.enum';
import LiteralsService from 'src/services/literals/LiteralsService';
import { useSyncingAccounts } from 'src/hooks/useSyncingAccounts';
import SkeletonLoader from 'src/components/atomic/atoms/skeletonLoader/SkeletonLoader';
import Selectors from 'src/utils/selectors';

interface BudgetProgressBarProps {
    progressPercent: number;
    secondaryProgressPercent?: number;
    overAmount: number;
    targetAmount: number;
    status: BudgetSummaryStatus;
    spentAmount: number;
}

export default function BudgetProgressBar({
    progressPercent,
    secondaryProgressPercent,
    overAmount,
    targetAmount,
    status,
    spentAmount
}: BudgetProgressBarProps): JSX.Element {
    const { isSyncingAccounts } = useSyncingAccounts();
    const overPercent: number = overAmount > 0 ? (overAmount / (targetAmount + overAmount)) * 100 : 0;
    const statusClassName: string =
        status === BudgetSummaryStatus.OVER
            ? styles.bad
            : status === BudgetSummaryStatus.AT_RISK
            ? styles.warning
            : styles.good;

    if (isSyncingAccounts) return <SkeletonLoader />;

    return (
        <div className={styles.progressBarRow}>
            <div
                className={styles.progressBar}
                aria-label={LiteralsService.getLiteral('Budget.Current.progressBarAriaLabel', [
                    spentAmount,
                    FormatService.formatPercent(progressPercent, 0)
                ])}
                role="img"
                data-cy={Selectors.SpendSmart.PROGRESS_BAR}
            >
                {secondaryProgressPercent > 0 && (
                    <div
                        className={`${styles.stripedProgressBar} ${statusClassName}`}
                        style={{ width: `${secondaryProgressPercent}%` }}
                    />
                )}
                <div
                    className={`${styles.coloredProgressBar} ${statusClassName}`}
                    style={{ width: `${progressPercent}%` }}
                    data-cy={Selectors.SpendSmart.COLORED_PROGRESS_BAR}
                />
            </div>
            <div className={styles.amountLabels} aria-hidden="true">
                {overPercent < 90 && <Typography variant={TypographyVariants.SUBTEXT}>$0</Typography>}
                <Typography
                    variant={TypographyVariants.SUBTEXT}
                    component="div"
                    className={styles.monthlyAmountLabel}
                    style={{ marginRight: `${overPercent}%` }}
                >
                    {overAmount > 0 && (
                        <div className={styles.monthlyAmountBackground}>
                            <div className={styles.monthlyAmountLine} />
                        </div>
                    )}
                    <p>{FormatService.format(targetAmount)}</p>
                </Typography>
            </div>
        </div>
    );
}
